import { createContext, useCallback, useContext, useMemo, useReducer } from "react";

// Rates the traveller has clicked Add on but not yet Continued.
//
// This is the ONE piece of booking state with no server or URL owner: nothing
// exists in msi_reservation_holds until Continue creates the group draft, and
// a list of (roomId, rateId, rooms_count) has no sensible URL encoding. Every
// other field that used to live here has been retired to the place that owns
// it:
//
//   version, flowStage   removed outright — no consumers
//   search / searchId    URL
//   cartId, groupId      URL path segments
//   stay                 URL query params (pre-cart), the cart (post-cart)
//   hotelSummary         useHotelDetails
//   modifyContext        removed — changes are the Change booking page's
//   liveCheck            cart.raw_live_check
//
// Nothing here survives a page load, and nothing is written to browser
// storage. See docs/reference/single-rate-booking.md.
//
// Why this is still mounted across the whole booking route group rather than
// scoped to the hotel detail page: a traveller who picks several rates,
// continues to the group review and presses Back must find their picks
// intact. Narrowing the provider would unmount it on that navigation and lose
// them — see GroupReviewLayout's docblock in BookingConfirmationPage.
//
// The rule that keeps it this small: if a fact has a server or URL
// representation, that is its only home. A field belongs here only when it
// exists nowhere else.
const initialState = {
  // One call to /api/reservation-groups/draft creates the group + all holds
  // atomically when Continue is pressed on the sticky cart bar. See
  // docs/booking/multi-rate-booking-implementation-plan.md.
  selectedRates: [],
};

// ── Identity stability ───────────────────────────────────────────────
//
// The normalizers below always return a fresh object — `{ ...value }` —
// even when the incoming data is identical to what is already in state.
// Without a bail-out, every dispatch produces a new state identity, and
// React compares hook dependencies by identity, not value. That is how a
// no-op dispatch turns into a re-render, and how a re-render that
// dispatches turns into an infinite loop.
//
// This bit us on 2026-08-21: BookingPaymentPage and BookingConfirmationPage
// each ran `useEffect(() => setFlowHotelSummary(booking.hotel), [booking])`,
// while `booking` was derived from `state.hotelSummary`. hotelSummary ->
// booking -> hotelSummary is a ring that can never settle while the reducer
// mints a new object each pass. React hit "Maximum update depth exceeded",
// and with no error boundary the tree unmounted, aborting the in-flight cart
// fetch (nginx logged hundreds of 499s) and remounting to do it again. It
// only showed on QA: on localhost the cart GET returns in 3ms and stabilises
// `booking` before the loop can outrun it, but at ~600ms the loop always wins.
//
// hotelSummary has since left this store entirely — the hotel comes from
// useHotelDetails — so that particular ring is now unrepresentable. The guard
// below stays for `stay`, which is still here and still derived from.
function bookingReducer(state, action) {
  switch (action.type) {
    // Starting a new search abandons any in-flight selection: a different
    // stay or destination invalidates the cart, the live-check, and every
    // selected rate. Named for what it does — the old `set-search` also
    // reset everything, but its name implied it only stored a value.
    case "start-new-search":
      return initialState;
    // ── Multi-rate booking actions (Phase 3) ─────────────────────
    case "add-selected-rate": {
      const rate = action.payload;
      if (!rate?.roomId || !rate?.rateId) return state;
      // Dedupe by (roomId, rateId, hotel). If already present, bump
      // rooms_count instead of adding a duplicate entry. This matches
      // the mental model "add another of the same room type."
      const rateKey = (r) => `${r.roomId}::${r.rateId}`;
      const target = rateKey(rate);
      const existingIdx = state.selectedRates.findIndex((r) => rateKey(r) === target);
      const nextRooms =
        Number.isInteger(rate.rooms_count) && rate.rooms_count > 0
          ? rate.rooms_count
          : 1;
      let selectedRates;
      if (existingIdx >= 0) {
        selectedRates = state.selectedRates.map((r, i) =>
          i === existingIdx
            ? { ...r, rooms_count: (r.rooms_count ?? 1) + nextRooms }
            : r
        );
      } else {
        selectedRates = [...state.selectedRates, { ...rate, rooms_count: nextRooms }];
      }
      return { ...state, selectedRates };
    }
    case "remove-selected-rate": {
      const target = action.payload;
      if (!target?.roomId || !target?.rateId) return state;
      const selectedRates = state.selectedRates.filter(
        (r) => !(r.roomId === target.roomId && r.rateId === target.rateId)
      );
      return { ...state, selectedRates };
    }
    case "set-selected-rate-count": {
      const { roomId, rateId, rooms_count } = action.payload ?? {};
      if (!roomId || !rateId) return state;
      // Zero or negative removes the entry entirely; matches the mental
      // model of a quantity picker on a cart line.
      if (!Number.isInteger(rooms_count) || rooms_count <= 0) {
        return {
          ...state,
          selectedRates: state.selectedRates.filter(
            (r) => !(r.roomId === roomId && r.rateId === rateId)
          ),
        };
      }
      // Same count in, same state reference out. `selectedRates` is the last
      // field in this store, and it is the last place the 2026-08-21 ring
      // could form: an effect deriving from it and dispatching back would
      // never settle if every dispatch minted a new state object. See
      // renderStability.test.jsx.
      const current = state.selectedRates.find(
        (r) => r.roomId === roomId && r.rateId === rateId
      );
      if (current && current.rooms_count === rooms_count) return state;
      return {
        ...state,
        selectedRates: state.selectedRates.map((r) =>
          r.roomId === roomId && r.rateId === rateId
            ? { ...r, rooms_count }
            : r
        ),
      };
    }
    case "clear-selected-rates":
      return { ...state, selectedRates: [] };
    case "reset":
      return initialState;
    default:
      return state;
  }
}

export const SelectedRatesContext = createContext(null);

export function SelectedRatesProvider({ children }) {
  // Starts empty on every page load. There is deliberately no hydrate
  // step: the previous sessionStorage hydrate had to run in useReducer's
  // lazy initializer rather than an effect, because child effects fire
  // before parent effects and a mount-time child dispatch would otherwise be
  // stomped by the parent's later hydrate. That whole hazard class is
  // gone once nothing is restored from storage.
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  const startNewSearch = useCallback(
    () => dispatch({ type: "start-new-search" }),
    []
  );
  const resetFlow = useCallback(() => dispatch({ type: "reset" }), []);

  // ── Multi-rate booking actions (Phase 3) ─────────────────────
  const addSelectedRate = useCallback(
    (payload) => dispatch({ type: "add-selected-rate", payload }),
    []
  );
  const removeSelectedRate = useCallback(
    (payload) => dispatch({ type: "remove-selected-rate", payload }),
    []
  );
  const setSelectedRateCount = useCallback(
    (payload) => dispatch({ type: "set-selected-rate-count", payload }),
    []
  );
  const clearSelectedRates = useCallback(
    () => dispatch({ type: "clear-selected-rates" }),
    []
  );

  const selectors = useMemo(
    () => ({
      // Multi-rate selectors (Phase 3)
      getSelectedRates: () => state.selectedRates ?? [],
      getSelectedRatesCount: () =>
        (state.selectedRates ?? []).reduce(
          (sum, r) => sum + (r.rooms_count ?? 1),
          0
        ),
      isRateSelected: (roomId, rateId) =>
        (state.selectedRates ?? []).some(
          (r) => r.roomId === roomId && r.rateId === rateId
        ),
      getSelectedRateCount: (roomId, rateId) => {
        const r = (state.selectedRates ?? []).find(
          (x) => x.roomId === roomId && x.rateId === rateId
        );
        return r?.rooms_count ?? 0;
      },
      isMultiRateMode: () => (state.selectedRates ?? []).length > 0,
    }),
    [state.selectedRates]
  );

  const value = useMemo(
    () => ({
      state,
      startNewSearch,
      resetFlow,
      // Multi-rate actions (Phase 3)
      addSelectedRate,
      removeSelectedRate,
      setSelectedRateCount,
      clearSelectedRates,
      selectors,
    }),
    [
      state,
      startNewSearch,
      resetFlow,
      addSelectedRate,
      removeSelectedRate,
      setSelectedRateCount,
      clearSelectedRates,
      selectors,
    ]
  );

  return <SelectedRatesContext.Provider value={value}>{children}</SelectedRatesContext.Provider>;
}

export function useSelectedRates() {
  const context = useContext(SelectedRatesContext);
  if (!context) {
    throw new Error("useSelectedRates must be used within a SelectedRatesProvider");
  }
  return context;
}
